import React, { PureComponent } from "react";
import { BrowserRouter, Route, Redirect, Switch } from "react-router-dom";
import { connect } from "react-redux";
import { logoutUser } from "../actions/authentication";
import { navigateToLogin } from "../actions/router";
import { checkAgreement, getHeapData, changeCulture, clearLoginLanguagePrompt } from "../actions/general";
import { useLanguageFromNowOn, keepCurrentLanguage, useLanguageThisVisit } from "../actions/languageChoice";
import { getCulture } from "../actions/culture";
import { idleAutoLogout, clearIdleInterval } from "../util/idleAutoLogout";
import { getStringHashCode } from "../util/helpers";
import wtI18n from "../util/wt-i18n";
import LanguageChangeModal from "../components/LanguageChangeModal";
import StartContainer from "./StartContainer";
import LoginContainer from "./LoginContainer";
import BatchesContainer from "./BatchesContainer";
import WidgetContainer from "./WidgetContainer";
import QuestionContainer from "./QuestionContainer";
import GdprContainer from "./GdprContainer";

const GdprWithLoginRoute = ({ component: Component, ...rest }) => {
	return (
		<Route
			{...rest}
			render={(props) => {
				if (!rest.authenticated) {
					return <Redirect to={{ pathname: `/${window.initialState.customer}` }} />;
				}
				if (rest.showGdprAgreement) {
					return (
						<Redirect
							to={{
								pathname: `/${window.initialState.customer}/gdpr`,
								state: { from: props.location },
							}}
						/>
					);
				}
				return <Component {...props} />;
			}}
		/>
	);
};

const LoginRoute = ({ component: Component, ...rest }) => {
	return (
		<Route
			{...rest}
			render={(props) => {
				if (!rest.authenticated) {
					return <Redirect to={{ pathname: `/${window.initialState.customer}` }} />;
				}
				return <Component {...props} />;
			}}
		/>
	);
};

class App extends PureComponent {
	componentDidMount() {
		this.props.persistCulture(this.props.culture);
		wtI18n.getResources(this.props.culture);

		if (this.props.authenticated) {
			this.props.checkAgreementValue();
			this.props.initHeap();
			idleAutoLogout(this.props.logout);
		}
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		if (nextProps.authenticated && !this.props.authenticated) {
			this.props.checkAgreementValue();
			this.props.initHeap();
			idleAutoLogout(this.props.logout);
		}

		if (!nextProps.authenticated && this.props.authenticated) {
			clearIdleInterval();
		}

		if (nextProps.culture !== this.props.culture) {
			wtI18n.getResources(nextProps.culture);
		}
	}

	// The three choices and the non-choice ("×"/backdrop) all close the prompt
	// first, so it can never be answered twice.
	handleUseFromNowOn = () => {
		const { newCulture } = this.props.loginLanguagePrompt;
		this.props.onClearLoginLanguagePrompt();
		this.props.onUseLanguageFromNowOn(newCulture);
	};

	handleKeep = () => {
		const { currentCulture } = this.props.loginLanguagePrompt;
		this.props.onClearLoginLanguagePrompt();
		this.props.onKeepCurrentLanguage(currentCulture);
	};

	handleJustThisVisit = () => {
		const { newCulture } = this.props.loginLanguagePrompt;
		this.props.onClearLoginLanguagePrompt();
		this.props.onUseLanguageThisVisit(newCulture);
	};

	render() {
		// The login prompt lives here rather than on the survey list so it is not
		// tied to a route: it has to survive the GDPR redirect and show before the
		// privacy notice, which then reads in the language just chosen.
		return (
			<div>
				{this.props.authenticated && this.props.loginLanguagePrompt && (
					<LanguageChangeModal
						context={this.props.loginLanguagePrompt.context}
						newCulture={this.props.loginLanguagePrompt.newCulture}
						currentCulture={this.props.loginLanguagePrompt.currentCulture}
						languages={this.props.languages}
						onUseFromNowOn={this.handleUseFromNowOn}
						onKeep={this.handleKeep}
						onJustThisVisit={this.handleJustThisVisit}
						onDismiss={this.props.onClearLoginLanguagePrompt}
					/>
				)}
				<BrowserRouter>
					<Switch>
						<Route exact path={"/"} component={StartContainer} />
						<Route exact path={"/" + window.initialState.customer} component={LoginContainer} />
						<LoginRoute
							exact
							path={"/" + window.initialState.customer + "/gdpr"}
							component={GdprContainer}
							authenticated={this.props.authenticated}
						/>
						<GdprWithLoginRoute
							exact
							path={"/" + window.initialState.customer + "/batches"}
							component={BatchesContainer}
							authenticated={this.props.authenticated}
							showGdprAgreement={this.props.showGdprAgreement}
						/>
						<GdprWithLoginRoute
							exact
							path={"/" + window.initialState.customer + "/temperature"}
							component={WidgetContainer}
							authenticated={this.props.authenticated}
							showGdprAgreement={this.props.showGdprAgreement}
						/>
						<GdprWithLoginRoute
							path={"/" + window.initialState.customer + "/batches/:id"}
							component={QuestionContainer}
							authenticated={this.props.authenticated}
							showGdprAgreement={this.props.showGdprAgreement}
						/>
					</Switch>
				</BrowserRouter>
			</div>
		);
	}
}

const mapStateToProps = (state) => {
	return {
		authenticated: state.authentication.authenticated,
		showGdprAgreement: state.general.showGdprAgreement,
		culture: getCulture(state),
		languages: state.general.languages,
		loginLanguagePrompt: state.general.loginLanguagePrompt,
	};
};

const mapDispatchToProps = (dispatch, { history }) => {
	return {
		checkAgreementValue: () => {
			dispatch(checkAgreement()).then((result) => {
				if (result.status === 401) {
					dispatch(logoutUser()).then(navigateToLogin(history));
				}
			});
		},
		initHeap: () => {
			if (!window.heap) return null;
			dispatch(getHeapData()).then((result) => {
				if (result.success) {
					window.heap.identify(getStringHashCode(result.visitor.id));
					window.heap.addUserProperties({
						RoleName: result.visitor.role,
						CustomerId: result.account.id,
						Language: result.visitor.language_override,
						CustomerPackage: result.account.planLevel,
					});
				}
			});
		},
		logout: () => dispatch(logoutUser()).then(navigateToLogin()),
		persistCulture: (culture) => dispatch(changeCulture(culture)),
		onUseLanguageFromNowOn: (newCulture) => dispatch(useLanguageFromNowOn(newCulture)),
		onKeepCurrentLanguage: (currentCulture) => dispatch(keepCurrentLanguage(currentCulture)),
		onUseLanguageThisVisit: (newCulture) => dispatch(useLanguageThisVisit(newCulture)),
		onClearLoginLanguagePrompt: () => dispatch(clearLoginLanguagePrompt()),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
